import { afterAnimationEnd, formatMoney } from '../utils/utils.js';
import Swiper from 'swiper';
import { Navigation, Pagination, Thumbs } from 'swiper/modules';

export class ProductPreview {
    constructor(container) {
        this.container = container;
        this.initializeElements();
        this.initializeTemplates();
        this.currentVariantId = null;
        this.currentProductHandle = null;
        this.isClosing = false;
        this.mainSwiper = null;
        this.thumbsSwiper = null;

        this.bindEvents();
    }

    initializeElements() {
        this.modal = this.container.querySelector('[data-preview-modal]');
        this.loading = this.container.querySelector('[data-preview-loading]');
        this.title = this.container.querySelector('[data-preview-title]');
        this.gallery = this.container.querySelector('[data-preview-gallery]');
        this.price = this.container.querySelector('[data-preview-price]');
        this.description = this.container.querySelector('[data-preview-description]');
        this.descriptionWrapper =
            this.description?.closest('[data-preview-description-wrapper]') ||
            this.description?.parentElement;
        this.features = this.container.querySelector('[data-preview-features]');
        this.addButton = this.container.querySelector('[data-preview-add]');
        this.addButtonText = this.container.querySelector('[data-preview-add-text]');
    }

    initializeTemplates() {
        this.templates = {
            swiper: this.container.querySelector('[data-preview-swiper-template]'),
            slide: this.container.querySelector('[data-preview-slide-template]'),
            thumb: this.container.querySelector('[data-preview-thumb-template]'),
            feature: this.container.querySelector('[data-preview-feature-template]'),
            soldOut: this.container.querySelector('[data-preview-sold-out-template]'),
            added: this.container.querySelector('[data-preview-added-template]')
        };
    }

    bindEvents() {
        // Close button events
        this.container.querySelectorAll('[data-preview-close]').forEach((btn) => {
            btn.addEventListener('click', () => this.close());
        });

        // Add to order button
        this.addButton?.addEventListener('click', () => this.handleAddToOrder());

        // Keyboard navigation
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen()) {
                this.close();
            }
        });
    }

    async show(productHandle, variantId) {
        if (this.isClosing) return;

        console.log('[Preview] Opening for:', productHandle, 'variant:', variantId);
        this.currentProductHandle = productHandle;
        this.currentVariantId = variantId;

        // Show modal with js-hidden pattern
        this.modal.classList.remove('js-hidden');
        this.modal.classList.add('is-opening');

        // Show loading
        this.showLoading();

        try {
            const product = await this.fetchProduct(productHandle);
            const variant = product.variants.find((v) => v.id == variantId) || product.variants[0];

            console.log('[Preview] Product:', product);
            console.log('[Preview] Variant:', variant);

            await this.updateContent(product, variant);

            // Hide loading after content is ready
            setTimeout(() => this.hideLoading(), 100);

            // Remove opening class after animation
            afterAnimationEnd(this.modal, () => {
                this.modal.classList.remove('is-opening');
            });
        } catch (error) {
            console.error('[Preview] Failed to load:', error);
            this.close();
        }
    }

    async fetchProduct(handle) {
        const response = await fetch(`/products/${handle}.js`);
        if (!response.ok) throw new Error('Failed to fetch product');
        return response.json();
    }

    async updateContent(product, variant) {
        // Title
        if (this.title) {
            this.title.textContent = product.title;
        }

        // Price
        if (this.price) {
            this.updatePrice(variant);
        }

        // Gallery
        if (this.gallery && product.images) {
            this.updateGallery(product.images, product.title);
        }

        // Description - ensure it's visible when content exists
        if (this.description && this.descriptionWrapper) {
            if (product.description && product.description.trim() !== '') {
                this.description.innerHTML = product.description;
                this.descriptionWrapper.classList.remove('hidden', 'js-hidden');
            } else {
                this.descriptionWrapper.classList.add('js-hidden');
            }
        }

        // Features
        if (this.features) {
            this.updateFeatures(product);
        }

        // Add button
        if (this.addButton) {
            this.updateAddButton(variant);
        }
    }

    updatePrice(variant) {
        const hasComparePrice = variant.compare_at_price && variant.compare_at_price > variant.price;

        const priceHtml = hasComparePrice
            ? `<span class="line-through text-gray-500 text-lg mr-2">${formatMoney(variant.compare_at_price)}</span>
               <span class="text-red-600">${formatMoney(variant.price)}</span>`
            : formatMoney(variant.price);

        this.price.innerHTML = priceHtml;
    }

    updateGallery(images, altText = '') {
        // Destroy existing swipers
        if (this.mainSwiper) {
            this.mainSwiper.destroy();
            this.mainSwiper = null;
        }
        if (this.thumbsSwiper) {
            this.thumbsSwiper.destroy();
            this.thumbsSwiper = null;
        }

        if (!images || images.length === 0) {
            this.gallery.innerHTML = '<div class="aspect-square bg-gray-100 rounded-lg"></div>';
            return;
        }

        // Clone swiper template
        const swiperTemplate = this.templates.swiper.content.cloneNode(true);
        const slideTemplate = this.templates.slide.content;
        const thumbTemplate = this.templates.thumb.content;

        // Get containers from template - they're at the root level
        const mainSwiper = swiperTemplate.querySelector('[data-preview-swiper]');
        const mainWrapper = mainSwiper.querySelector('.swiper-wrapper');
        const thumbsContainer = swiperTemplate.querySelector('[data-preview-thumbnails]');
        const thumbsSwiper = thumbsContainer?.querySelector('[data-preview-thumbs-swiper]');
        const thumbsWrapper = thumbsSwiper?.querySelector('.swiper-wrapper');

        // Create slides
        images.forEach((image, index) => {
            // Main slide
            const slide = slideTemplate.cloneNode(true);
            const img = slide.querySelector('img');
            img.src = image;
            img.alt = `${altText} ${index + 1}`;
            if (index === 0) img.loading = 'eager';
            mainWrapper.appendChild(slide);

            // Thumbnail slide (desktop only, max 4)
            if (thumbsWrapper && index < 4) {
                const thumb = thumbTemplate.cloneNode(true);
                const thumbImg = thumb.querySelector('img');
                thumbImg.src = image;
                thumbImg.alt = `${altText} thumbnail ${index + 1}`;
                thumbsWrapper.appendChild(thumb);
            }
        });

        // Clear gallery and append new content
        this.gallery.innerHTML = '';
        this.gallery.appendChild(swiperTemplate);

        // Initialize Swiper after DOM is ready
        requestAnimationFrame(() => {
            this.initializeSwipers(images.length);
        });
    }

    initializeSwipers(slideCount) {
        // Initialize thumbs swiper first (if multiple images)
        if (slideCount > 1) {
            const thumbsElement = this.gallery.querySelector('[data-preview-thumbs-swiper]');
            if (thumbsElement) {
                this.thumbsSwiper = new Swiper(thumbsElement, {
                    modules: [Navigation],
                    spaceBetween: 8,
                    slidesPerView: 4,
                    freeMode: true,
                    watchSlidesProgress: true
                });
            }
        }

        // Initialize main swiper
        const mainElement = this.gallery.querySelector('[data-preview-swiper]');
        if (mainElement) {
            const config = {
                modules: [Pagination],
                spaceBetween: 0,
                slidesPerView: 1,
                loop: false,
                watchOverflow: true
            };

            // Add pagination for mobile if multiple slides
            if (slideCount > 1) {
                config.pagination = {
                    el: mainElement.querySelector('.swiper-pagination'),
                    clickable: true,
                    bulletClass: 'w-2 h-2 rounded-full bg-gray-300 transition-all cursor-pointer',
                    bulletActiveClass: 'bg-gray-900 w-6',
                    renderBullet: function (index, className) {
                        return `<button class="${className}" aria-label="Go to slide ${index + 1}"></button>`;
                    }
                };
            }

            // Add thumbs module if available
            if (this.thumbsSwiper) {
                config.modules.push(Thumbs);
                config.thumbs = {
                    swiper: this.thumbsSwiper
                };
            }

            this.mainSwiper = new Swiper(mainElement, config);
        }
    }

    updateFeatures(product) {
        const features = [];

        // Add any metafields if available
        if (product.metafields?.specifications) {
            Object.entries(product.metafields.specifications).forEach(([key, value]) => {
                if (value) {
                    features.push({
                        label: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
                        value
                    });
                }
            });
        }

        if (features.length > 0) {
            // Clear existing content
            this.features.innerHTML = '<h4 class="font-semibold text-gray-900 mb-3">Details</h4>';

            // Use template for each feature
            const featureTemplate = this.templates.feature.content;
            features.forEach((feature) => {
                const featureEl = featureTemplate.cloneNode(true);
                featureEl.querySelector('[data-feature-label]').textContent = feature.label;
                featureEl.querySelector('[data-feature-value]').textContent = feature.value;
                this.features.appendChild(featureEl);
            });

            this.features.classList.remove('hidden', 'js-hidden');
        } else {
            this.features.classList.add('js-hidden');
        }
    }

    updateAddButton(variant) {
        const footer = this.addButton.parentElement;

        // Check availability
        console.log('[Preview] Checking availability:', variant.available);
        if (variant.available === false) {
            // Replace with sold out button
            const soldOutTemplate = this.templates.soldOut.content.cloneNode(true);
            footer.innerHTML = '';
            footer.appendChild(soldOutTemplate);

            // Re-get reference to button
            this.addButton = footer.querySelector('button');
        } else {
            // Ensure normal add button is shown
            if (!this.addButton || this.addButton.disabled) {
                // Restore normal button if it was replaced
                footer.innerHTML = '';
                const button = document.createElement('button');
                button.type = 'button';
                button.setAttribute('data-preview-add', '');
                button.className =
                    'w-full py-4 px-6 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-3';
                button.innerHTML = `
                    ${this.getIconHTML('plus', 20)}
                    <span data-preview-add-text>${this.addButtonText?.textContent || 'Add to order'}</span>
                `;
                footer.appendChild(button);

                // Re-bind event and update reference
                this.addButton = button;
                this.addButton.addEventListener('click', () => this.handleAddToOrder());
            }
        }
    }

    getIconHTML(iconName, size = 20) {
        // Get icon HTML from the rendered icon snippet
        const iconElement = this.container.querySelector(`[data-icon-${iconName}]`);
        if (iconElement) {
            const svg = iconElement.querySelector('svg');
            if (svg) {
                const clonedSvg = svg.cloneNode(true);
                clonedSvg.setAttribute('width', size);
                clonedSvg.setAttribute('height', size);
                return clonedSvg.outerHTML;
            }
        }
        return '';
    }

    showLoading() {
        if (this.loading) {
            this.loading.style.display = 'flex';
            this.loading.classList.remove('js-hidden');
        }
    }

    hideLoading() {
        if (this.loading) {
            this.loading.style.display = 'none';
            this.loading.classList.add('js-hidden');
        }
    }

    isOpen() {
        return this.modal && !this.modal.classList.contains('js-hidden');
    }

    async close() {
        if (!this.isOpen() || this.isClosing) return;

        this.isClosing = true;
        this.modal.classList.add('is-closing');

        await afterAnimationEnd(this.modal);

        this.modal.classList.add('js-hidden');
        this.modal.classList.remove('is-closing');
        this.isClosing = false;

        // Reset state
        this.currentVariantId = null;
        this.currentProductHandle = null;

        // Destroy swipers
        if (this.mainSwiper) {
            this.mainSwiper.destroy();
            this.mainSwiper = null;
        }
        if (this.thumbsSwiper) {
            this.thumbsSwiper.destroy();
            this.thumbsSwiper = null;
        }

        // Clean up content
        this.hideLoading();
        if (this.gallery) this.gallery.innerHTML = '';
        if (this.description) this.description.innerHTML = '';
        if (this.features) this.features.innerHTML = '';
    }

    handleAddToOrder() {
        if (!this.currentVariantId || this.addButton.disabled) return;

        // Find and check the corresponding checkbox
        const checkbox = this.container.querySelector(
            `input[name="upsell"][value="${this.currentVariantId}"]`
        );
        if (checkbox && !checkbox.checked) {
            checkbox.checked = true;
            checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Show success state
        this.showAddSuccess();

        // Close after a short delay
        setTimeout(() => this.close(), 1500);
    }

    showAddSuccess() {
        const footer = this.addButton.parentElement;
        const originalButton = this.addButton.cloneNode(true);

        // Replace with success button from template
        const addedTemplate = this.templates.added.content.cloneNode(true);
        footer.innerHTML = '';
        footer.appendChild(addedTemplate);

        // Don't restore if we're closing
        setTimeout(() => {
            if (!this.isClosing && footer.querySelector('button')) {
                footer.innerHTML = '';
                footer.appendChild(originalButton);
                this.addButton = originalButton;
                this.addButton.addEventListener('click', () => this.handleAddToOrder());
            }
        }, 3000);
    }
}
